/**
 * How one table is drawn, where it parts from the document's tables.
 *
 * The document sets how every table is ruled, how much air a row gets and
 * whether rows are banded (`domain/layout/tables.ts`), and that is what keeps
 * forty tables reading as one report. A table may still be set apart: a
 * statement with a filled head, a schedule ruled all over, a summary whose last
 * row is its total. Each of those is a setting here.
 *
 * Every setting is optional. Absent, the table follows the document, and goes
 * on following it when the document changes; set, it holds for this table
 * alone. Nothing here touches what a cell says: a look that rewrote a figure
 * would be a look that could put a wrong number in a signed document.
 *
 * Applied as classes and as the same CSS custom properties the document's
 * settings use, set on the table itself, so the page's one set of table rules
 * draws both (see `builder.css`).
 *
 * Pure. Values in, classes and properties out.
 */

import { isJsonObject, type JsonValue } from '@/domain/json'
import { isAccent, readableInk } from '@/domain/layout/colour'
import {
  TABLE_DENSITIES,
  TABLE_DENSITY_IDS,
  TABLE_HEAD_IDS,
  TABLE_RULES,
  TABLE_RULE_IDS,
  TABLE_ZEBRA,
  type TableDensity,
  type TableHead,
  type TableRule,
} from '@/domain/layout/tables'

export { readableInk }
export type { TableHead }

/** How the head row can be drawn, the document's choice unless a table makes its own. */
export const TABLE_HEADS = TABLE_HEAD_IDS

/** How a negative figure is written: with its sign, or in brackets as a printed statement has it. */
export const TABLE_NEGATIVES = ['minus', 'parentheses'] as const
export type TableNegatives = (typeof TABLE_NEGATIVES)[number]

/** How the head's words are cased: as written, or in capitals. Only drawn so: the cell keeps its words. */
export const TABLE_HEAD_CASES = ['normal', 'upper'] as const
export type TableHeadCase = (typeof TABLE_HEAD_CASES)[number]

/** How a total row is set apart: a tinted band ruled above and below, or bold alone. */
export const TABLE_TOTALS = ['fill', 'bold'] as const
export type TableTotals = (typeof TABLE_TOTALS)[number]

export interface TableLook {
  readonly head?: TableHead
  /** `#rrggbb`: the head's rule, wash or fill, and the total's rule. The document accent otherwise. */
  readonly colour?: string
  /** Lines, in place of the document's. */
  readonly rules?: TableRule
  /** Row height, in place of the document's. */
  readonly density?: TableDensity
  /** Alternate rows banded, or not, in place of the document's choice. */
  readonly banded?: boolean
  /** The first column as the row labels, in bold. */
  readonly labels?: boolean
  /** The last row as the table's total: bold, ruled above. */
  readonly total?: boolean
  /** Negative figures in brackets, `(1 467)` for `-1 467`. Only how they are shown: the cell keeps its sign. */
  readonly negatives?: TableNegatives
  /** The head in capitals, drawn only: the cell keeps the words it was given. */
  readonly headCase?: TableHeadCase
  /** The rows that are totals (a bold cell in them, from CSRD's design or the author's), set apart. */
  readonly totals?: TableTotals
  /** `#rrggbb`: the band behind the head (when tinted) and the totals (when filled). Derived from the colour otherwise. */
  readonly tint?: string
}

function oneOf<T extends string>(options: readonly T[], value: unknown): T | undefined {
  return typeof value === 'string' && (options as readonly string[]).includes(value)
    ? (value as T)
    : undefined
}

/**
 * A table's look, read off a stored table.
 *
 * Forgiving setting by setting, as every reader of a stored document is: one
 * that is not valid is dropped and the rest kept. A colour is kept only when it
 * is a six-digit hex, since it is written into a stylesheet.
 */
export function readTableLook(stored: JsonValue): TableLook {
  const source = isJsonObject(stored) ? stored['look'] : undefined
  if (!isJsonObject(source)) return {}

  const head = oneOf(TABLE_HEADS, source['head'])
  const rules = oneOf(TABLE_RULE_IDS, source['rules'])
  const density = oneOf(TABLE_DENSITY_IDS, source['density'])
  const negatives = oneOf(TABLE_NEGATIVES, source['negatives'])
  const headCase = oneOf(TABLE_HEAD_CASES, source['headCase'])
  const totals = oneOf(TABLE_TOTALS, source['totals'])
  const tint = source['tint']
  const colour = source['colour']
  const flag = (key: string): boolean | undefined =>
    typeof source[key] === 'boolean' ? source[key] : undefined
  const banded = flag('banded')
  const labels = flag('labels')
  const total = flag('total')

  return {
    ...(head ? { head } : {}),
    ...(typeof colour === 'string' && isAccent(colour) ? { colour: colour.toLowerCase() } : {}),
    ...(rules ? { rules } : {}),
    ...(density ? { density } : {}),
    ...(banded !== undefined ? { banded } : {}),
    ...(labels !== undefined ? { labels } : {}),
    ...(total !== undefined ? { total } : {}),
    ...(negatives ? { negatives } : {}),
    ...(headCase ? { headCase } : {}),
    ...(totals ? { totals } : {}),
    ...(typeof tint === 'string' && isAccent(tint) ? { tint: tint.toLowerCase() } : {}),
  }
}

/** A look with one setting changed, or taken off when `value` is undefined: back to the document's. */
export function withLook<K extends keyof TableLook>(
  look: TableLook,
  key: K,
  value: TableLook[K] | undefined,
): TableLook {
  const next: Record<string, unknown> = { ...look }
  if (value === undefined) delete next[key]
  else next[key] = value
  return next
}

/**
 * The classes a table wears for its look. A head class only where the table
 * has a head of its own: otherwise it draws the document's (see `layoutStyle`).
 */
export function lookClasses(look: TableLook): string[] {
  return [
    look.head ? `head--${look.head}` : '',
    look.labels ? 'has-labels' : '',
    look.total ? 'has-total' : '',
    look.headCase === 'upper' ? 'head-case--upper' : '',
    look.totals ? `totals--${look.totals}` : '',
    look.tint ? 'has-tint' : '',
  ].filter(Boolean)
}

/**
 * The custom properties a table sets for its look, over the document's.
 *
 * `accent` and `documentHead` are the document's. The ink of a filled head is
 * worked out from the colour it is filled with, the table's own or the
 * accent, whichever head the table ends up with.
 */
export function lookStyle(
  look: TableLook,
  accent: string,
  documentHead: TableHead = 'underline',
): Record<string, string> {
  const style: Record<string, string> = {}
  if (look.colour) style['--table-head'] = look.colour
  if (look.head || look.colour) {
    const filled = (look.head ?? documentHead) === 'filled'
    style['--table-head-text'] = filled ? readableInk(look.colour ?? accent) : 'currentColor'
  }
  if (look.rules) {
    const rule = TABLE_RULES[look.rules]
    style['--table-outer'] = rule.outer
    style['--table-inner'] = rule.inner
    style['--table-row'] = rule.row
  }
  if (look.density) {
    const density = TABLE_DENSITIES[look.density]
    style['--table-pad'] = density.padding
    style['--table-leading'] = density.leading
  }
  if (look.tint) style['--table-tint'] = look.tint
  if (look.banded !== undefined) style['--table-zebra'] = look.banded ? TABLE_ZEBRA : 'transparent'
  return style
}

/** Whether a look parts from the document at all, which the inspector says. */
export function hasOwnLook(look: TableLook | undefined): boolean {
  return look !== undefined && Object.keys(look).length > 0
}
